import { useState } from "react";
import { format, parseISO } from "date-fns";
import { nb } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { useAbsences, useDeleteAbsence } from "@/hooks/use-absences";
import { formatHours } from "@/lib/format";
import {
  ABSENCE_TYPE_DOTS as ABSENCE_DOTS,
  ABSENCE_TYPE_LABELS as ABSENCE_LABELS,
  absenceDisplayHours,
  filterAbsencesInRange,
} from "@/lib/absence-display";
import { toast } from "sonner";
import { SrIcon } from "@/components/ui/SrIcon";
import { AbsenceForm } from "@/components/shared/AbsenceForm";
import { formatAbsenceTimeRange } from "@/lib/absence-time";

interface CloseMonthAbsenceProps {
  userId: string | null;
  year: number;
  month: number;
}

const MONTH_NAMES = [
  "",
  "januar",
  "februar",
  "mars",
  "april",
  "mai",
  "juni",
  "juli",
  "august",
  "september",
  "oktober",
  "november",
  "desember",
];

export default function CloseMonthAbsence({
  userId,
  year,
  month,
}: CloseMonthAbsenceProps) {
  const { data: allAbsences = [] } = useAbsences(userId);
  const { mutate: deleteAbsence } = useDeleteAbsence();
  const [showForm, setShowForm] = useState(false);

  // Filter absences to this month
  const monthStart = `${year}-${String(month).padStart(2, "0")}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const monthEnd = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;

  const absences = filterAbsencesInRange(allAbsences, monthStart, monthEnd);

  const monthName = MONTH_NAMES[month] || "";

  const handleDelete = (id: string) => {
    if (!userId) return;
    deleteAbsence(
      { id, user_id: userId },
      { onSuccess: () => toast.success("Fravær slettet") }
    );
  };

  return (
    <div className="rounded-lg border border-border/50 bg-card p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold">
          Fravær i {monthName} {year}
        </h3>
        {!showForm && (
          <Button
            size="sm"
            variant="outline"
            className="h-10 flex-none text-sm xl:h-8 xl:text-xs"
            onClick={() => setShowForm(true)}
          >
            <SrIcon name="plus" className="mr-1.5 h-3.5 w-3.5" />
            Meld fravær
          </Button>
        )}
      </div>

      {/* VIB-1221: delt AbsenceForm (syk/ferie-toggle inne i skjemaet selv) */}
      {showForm && userId && (
        <div className="mb-3">
          <AbsenceForm
            userId={userId}
            autoFocus
            onSuccess={() => setShowForm(false)}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      {/* Absence list */}
      {absences.length > 0 ? (
        <div className="space-y-1.5">
          {absences.map((absence) => (
            <div
              key={absence.id}
              className="flex items-center justify-between gap-2 text-sm xl:text-xs p-2.5 rounded-lg border border-border/30 bg-background/50 group"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${ABSENCE_DOTS[absence.type]}`}
                />
                <span className="font-medium">{ABSENCE_LABELS[absence.type]}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-muted-foreground whitespace-nowrap tabular-nums">
                  {formatHours(absenceDisplayHours(absence))}
                </span>
                <span className="text-muted-foreground whitespace-nowrap">
                  {format(parseISO(absence.start_date), "dd.MM", { locale: nb })}
                  {formatAbsenceTimeRange(absence.start_time, absence.end_time) ? (
                    <> kl. {formatAbsenceTimeRange(absence.start_time, absence.end_time)}</>
                  ) : absence.start_date !== absence.end_date ? (
                    <>
                      {" – "}
                      {format(parseISO(absence.end_date), "dd.MM", { locale: nb })}
                    </>
                  ) : null}
                </span>
                <button
                  onClick={() => handleDelete(absence.id)}
                  className="w-9 h-9 xl:w-7 xl:h-7 flex items-center justify-center hover:bg-destructive/10 rounded xl:opacity-0 xl:group-hover:opacity-100 xl:focus-visible:opacity-100 xl:group-focus-within:opacity-100 transition-opacity"
                  aria-label="Slett"
                >
                  <SrIcon name="x" className="w-4 h-4 xl:w-3 xl:h-3 text-destructive"  />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm xl:text-xs text-muted-foreground py-1">
          Ingen fravær registrert denne måneden.
        </p>
      )}
    </div>
  );
}
